/**
 * The Google Maps list link, kept on this device for one trip.
 *
 * The server keeps the trip's link too, but only a trip admin's: `trip_update` lets nobody else
 * change the trip, so for every other member the link was never stored and the next sync asked for
 * it again. Keeping it here is what makes a sync one tap for them as well — and it is the member's
 * own copy, so clearing the site data clears it, and it never reaches anybody else on the trip.
 *
 * Per trip, because a member on two trips has two lists. Every read and write is wrapped: a
 * private window or blocked site data makes storage throw, and a sync must still work there,
 * simply with an empty field.
 */
const keyFor = (tripId: string) => `trip.poi-list-link.${tripId}`;

/** The link this device last synced this trip with, or "" where there is none. */
export function savedListLink(tripId: string): string {
  try {
    return localStorage.getItem(keyFor(tripId)) ?? "";
  } catch {
    return "";
  }
}

/** Keeps the link for the next sync; an empty one forgets what was kept. */
export function rememberListLink(tripId: string, link: string): void {
  try {
    const keep = link.trim();
    if (keep === "") localStorage.removeItem(keyFor(tripId));
    else localStorage.setItem(keyFor(tripId), keep);
  } catch {
    // A device that will not keep it still syncs; the link is simply typed again next time.
  }
}
